import { databases, DATABASE_ID, COLLECTION_ID_INVOICE_TRACKING } from '../config/appwrite';
import { ID, Query } from 'appwrite';

/**
 * Invoice Tracking Service
 * Handles all database operations for Invoice Tracking
 * Collection Name: "Invoices tracking SGM"
 * Collection ID: "InvoicesTrackingSGM" (Appwrite doesn't allow spaces in collection IDs)
 */

// Helper function to parse currency string to number
const parseCurrency = (currencyString) => {
  if (!currencyString) return 0;
  const cleaned = currencyString.replace(/TZS\s?/gi, '').replace(/,/g, '').trim();
  return parseFloat(cleaned) || 0;
};

// Get all invoices - returns empty array on any error so UI still loads
export const getAllInvoices = async () => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_INVOICE_TRACKING,
      [
        Query.orderDesc('$createdAt'),
        Query.limit(1000)
      ]
    );
    return response.documents.map(formatInvoice);
  } catch (error) {
    console.error('Error fetching invoices:', error);
    
    // Build user-friendly error with fix instructions
    const msg = (error.message || '').toString();
    const isCors = msg.includes('CORS') || msg.includes('cross-origin') || error.type === 'network_error' || msg.includes('Failed to fetch');
    const is404 = error.code === 404 || msg.includes('not found') || msg.includes('Collection');
    
    if (is404) {
      console.warn('Invoice tracking collection not found.');
      return [];
    }
    
    if (isCors) {
      const err = new Error('Add your domain in Appwrite: Console → Settings → Platforms → Add Platform (Web) → Hostname: sagemcomlogistics.com (no https://)');
      err.code = 'CORS_ERROR';
      throw err;
    }
    
    // For any other error, throw with original message so user sees something useful
    const friendly = new Error(msg || 'Cannot reach Appwrite. Add sagemcomlogistics.com in Appwrite Platforms (hostname without https://).');
    friendly.code = error.code || 'FETCH_ERROR';
    throw friendly;
  }
};

// Normalize invoice IDs for duplicate checks (trim + uppercase).
export const normalizeInvoiceId = (invoiceId) =>
  String(invoiceId || '').trim().toUpperCase();

// Check if invoice ID already exists (case-insensitive check)
export const checkInvoiceExists = async (invoiceId, excludeDocumentId = '') => {
  try {
    const normalizedInputId = normalizeInvoiceId(invoiceId);
    if (!normalizedInputId) {
      return false;
    }
    
    // Get all invoices and check case-insensitively
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_INVOICE_TRACKING,
      [
        Query.limit(1000)
      ]
    );
    
    const exists = response.documents.some(doc => {
      if (excludeDocumentId && doc.$id === excludeDocumentId) return false;
      const docInvoiceId = normalizeInvoiceId(doc.invoiceId);
      return docInvoiceId === normalizedInputId;
    });
    
    return exists;
  } catch (error) {
    console.error('Error checking invoice existence:', error);
    // If collection doesn't exist, return false
    if (error.code === 404 || error.message?.includes('not found') || error.message?.includes('Collection')) {
      return false;
    }
    throw error;
  }
};

// Get a single invoice by ID
export const getInvoiceById = async (id) => {
  try {
    const response = await databases.getDocument(
      DATABASE_ID,
      COLLECTION_ID_INVOICE_TRACKING,
      id
    );
    return formatInvoice(response);
  } catch (error) {
    console.error('Error fetching invoice:', error);
    throw error;
  }
};

// Build payload for Appwrite (string lengths match collection attributes).
const buildInvoiceDocumentPayload = (invoiceData, options = { includePrNo: true }) => {
  const invoiceId = (invoiceData.id || invoiceData.invoiceId || '').trim();
  const prNoTrimmed = invoiceData.prNo != null ? String(invoiceData.prNo).trim() : '';

  const documentData = {
    invoiceId,
    lot: String(invoiceData.lot || '').slice(0, 255),
    subcategory: String(invoiceData.subcategory || '').slice(0, 255),
    poId: String(invoiceData.poId || '').slice(0, 255),
    vendor: String(invoiceData.vendor || '').slice(0, 255),
    description: String(invoiceData.description || '').slice(0, 1000),
    amount: parseCurrency(invoiceData.amount || '0'),
    vat: parseCurrency(invoiceData.vat || '0'),
    totalAmount: parseCurrency(invoiceData.totalAmount || '0'),
    status: String(invoiceData.status || 'Admin review').slice(0, 50),
    level: String(invoiceData.level || '').slice(0, 64),
    invoicedDate: String(invoiceData.invoicedDate || '').slice(0, 255),
    dueDate: String(invoiceData.dueDate || '').slice(0, 255),
    paidDate: String(invoiceData.paidDate || '').slice(0, 255)
  };

  if (options.includePrNo) {
    documentData.prNo = prNoTrimmed.slice(0, 64);
  }

  return documentData;
};

function isLikelyMissingPrNoAttributeError(error) {
  const msg = String(error?.message || '').toLowerCase();
  const type = String(error?.type || '').toLowerCase();
  if (msg.includes('prno') || msg.includes('pr_no')) return true;
  if (msg.includes('unknown attribute')) return true;
  if (msg.includes('attribute') && (msg.includes('could not be found') || msg.includes('not found'))) return true;
  if (msg.includes('invalid document structure') && msg.includes('unknown')) return true;
  if (type.includes('document_invalid_structure')) return true;
  return false;
}

function isLikelyMissingLevelAttributeError(error) {
  const msg = String(error?.message || '').toLowerCase();
  if (msg.includes('level') && msg.includes('attribute')) return true;
  if (msg.includes('unknown attribute') && msg.includes('level')) return true;
  return false;
}

function isLikelyMissingOptionalAttributeError(error, key) {
  if (key === 'prNo') return isLikelyMissingPrNoAttributeError(error);
  if (key === 'level') return isLikelyMissingLevelAttributeError(error);
  return false;
}

// Create a new invoice
export const createInvoice = async (invoiceData) => {
  try {
    const invoiceId = (invoiceData.id || invoiceData.invoiceId || '').trim();

    // Check if invoice ID already exists (case-insensitive)
    if (invoiceId) {
      const exists = await checkInvoiceExists(invoiceId);
      if (exists) {
        throw new Error(`Invoice with ID "${invoiceId}" already exists. Please use a different invoice number.`);
      }
    }

    let documentData = buildInvoiceDocumentPayload(invoiceData, { includePrNo: true });

    let response;
    try {
      response = await databases.createDocument(
        DATABASE_ID,
        COLLECTION_ID_INVOICE_TRACKING,
        ID.unique(),
        documentData
      );
    } catch (firstError) {
      const hadPrNo = Object.prototype.hasOwnProperty.call(documentData, 'prNo');
      if (hadPrNo && isLikelyMissingOptionalAttributeError(firstError, 'prNo')) {
        console.warn('Retrying invoice create without prNo (add attribute via npm run add:invoice-tracking-pr-no):', firstError.message);
        documentData = buildInvoiceDocumentPayload(invoiceData, { includePrNo: false });
        response = await databases.createDocument(
          DATABASE_ID,
          COLLECTION_ID_INVOICE_TRACKING,
          ID.unique(),
          documentData
        );
      } else {
        throw firstError;
      }
    }

    return formatInvoice(response);
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

// Update an invoice
export const updateInvoice = async (id, invoiceData) => {
  try {
    const documentData = {};

    if (invoiceData.id !== undefined) {
      const nextInvoiceId = String(invoiceData.id).trim();
      if (nextInvoiceId) {
        const exists = await checkInvoiceExists(nextInvoiceId, id);
        if (exists) {
          throw new Error(`Invoice with ID "${nextInvoiceId}" already exists. Please use a different invoice number.`);
        }
      }
      documentData.invoiceId = nextInvoiceId.slice(0, 255);
    }
    if (invoiceData.lot !== undefined) documentData.lot = String(invoiceData.lot).slice(0, 255);
    if (invoiceData.subcategory !== undefined) documentData.subcategory = String(invoiceData.subcategory).slice(0, 255);
    if (invoiceData.poId !== undefined) documentData.poId = String(invoiceData.poId).slice(0, 255);
    if (invoiceData.vendor !== undefined) documentData.vendor = String(invoiceData.vendor).slice(0, 255);
    if (invoiceData.description !== undefined) documentData.description = String(invoiceData.description).slice(0, 1000);
    if (invoiceData.amount !== undefined) documentData.amount = parseCurrency(invoiceData.amount);
    if (invoiceData.vat !== undefined) documentData.vat = parseCurrency(invoiceData.vat);
    if (invoiceData.totalAmount !== undefined) documentData.totalAmount = parseCurrency(invoiceData.totalAmount);
    if (invoiceData.status !== undefined) documentData.status = String(invoiceData.status).slice(0, 50);
    if (invoiceData.level !== undefined) documentData.level = String(invoiceData.level ?? '').slice(0, 64);
    if (invoiceData.invoicedDate !== undefined) documentData.invoicedDate = String(invoiceData.invoicedDate).slice(0, 255);
    if (invoiceData.dueDate !== undefined) documentData.dueDate = String(invoiceData.dueDate).slice(0, 255);
    if (invoiceData.paidDate !== undefined) documentData.paidDate = String(invoiceData.paidDate).slice(0, 255);

    const prTrimmed = invoiceData.prNo !== undefined ? String(invoiceData.prNo ?? '').trim() : null;
    if (prTrimmed !== null) {
      documentData.prNo = prTrimmed.slice(0, 64);
    }

    const levelTrimmed = invoiceData.level !== undefined ? String(invoiceData.level ?? '').trim() : null;
    if (levelTrimmed !== null) {
      documentData.level = levelTrimmed.slice(0, 64);
    }

    if (Object.keys(documentData).length === 0) {
      throw new Error('Nothing to update. Provide at least one field (e.g. PR No or Pending level).');
    }

    const attemptUpdate = async (payload) =>
      databases.updateDocument(DATABASE_ID, COLLECTION_ID_INVOICE_TRACKING, id, payload);

    const stripOptionalMissingKeys = (payload, keys) => {
      let next = { ...payload };
      for (const key of keys) {
        if (!Object.prototype.hasOwnProperty.call(next, key)) continue;
        // eslint-disable-next-line no-unused-vars
        const { [key]: _omit, ...rest } = next;
        next = rest;
      }
      return next;
    };

    let response;
    try {
      response = await attemptUpdate(documentData);
    } catch (firstError) {
      const optionalKeys = ['prNo', 'level'].filter((key) =>
        Object.prototype.hasOwnProperty.call(documentData, key)
      );
      const missingKey = optionalKeys.find((key) =>
        isLikelyMissingOptionalAttributeError(firstError, key)
      );

      if (missingKey) {
        const onlyOptional =
          optionalKeys.length === 1 && Object.keys(documentData).length === 1;
        if (onlyOptional) {
          throw new Error(
            missingKey === 'level'
              ? 'Pending level cannot be saved yet: the Appwrite attribute "level" is missing on InvoicesTrackingSGM. Run: npm run add:invoice-tracking-level'
              : 'PR No cannot be saved yet: the Appwrite attribute "prNo" is missing on InvoicesTrackingSGM. Run: npm run add:invoice-tracking-pr-no'
          );
        }
        console.warn(`Retrying invoice update without ${missingKey}:`, firstError.message);
        const rest = stripOptionalMissingKeys(documentData, [missingKey]);
        if (Object.keys(rest).length === 0) {
          throw new Error(
            missingKey === 'level'
              ? 'Pending level cannot be saved yet: the Appwrite attribute "level" is missing on InvoicesTrackingSGM. Run: npm run add:invoice-tracking-level'
              : 'PR No cannot be saved yet: the Appwrite attribute "prNo" is missing on InvoicesTrackingSGM. Run: npm run add:invoice-tracking-pr-no'
          );
        }
        response = await attemptUpdate(rest);
      } else if (
        String(firstError?.message || '')
          .toLowerCase()
          .includes('document data and permissions are missing')
      ) {
        throw new Error(
          'Could not update invoice (empty update). If you are saving PR No, run: npm run add:invoice-tracking-pr-no'
        );
      } else {
        throw firstError;
      }
    }

    return formatInvoice(response);
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
};

// Delete an invoice
export const deleteInvoice = async (id) => {
  try {
    await databases.deleteDocument(
      DATABASE_ID,
      COLLECTION_ID_INVOICE_TRACKING,
      id
    );
    return { success: true };
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
};

// Format invoice from Appwrite document format to app format
const formatInvoice = (doc) => {
  // Helper to format currency
  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return `TZS ${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return amount || 'TZS 0';
  };

  return {
    id: doc.invoiceId || doc.$id, // Use invoiceId from DB or fallback to document ID
    documentId: doc.$id, // Store the actual document ID for updates/deletes
    lot: doc.lot || '',
    subcategory: doc.subcategory || '',
    poId: doc.poId || '',
    prNo: doc.prNo != null && doc.prNo !== undefined ? String(doc.prNo) : '',
    vendor: doc.vendor || '',
    description: doc.description || '',
    amount: formatCurrency(doc.amount),
    vat: formatCurrency(doc.vat),
    totalAmount: formatCurrency(doc.totalAmount),
    status: doc.status || 'Admin review',
    pendingLevel: doc.level != null && doc.level !== undefined ? String(doc.level) : '',
    invoicedDate: doc.invoicedDate || '',
    dueDate: doc.dueDate || '',
    paidDate: doc.paidDate || ''
  };
};
